import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { reduce } from 'lodash';
import qs from 'query-string';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import * as cfaActionCreators from '../../../../../actions/cfaActionCreators';
import GuidedTour from '../../../../Shared/GuidedTour/GuidedTour';
import { currencySymbol } from '../../../../../utils/formatters';

// GraphQL mutation for saving CFA changes
const SAVE_CFA = gql`
  mutation updateCfa($id: ID,
      $name: String,
      $cashFlowLength: Int,
      $cashFlowProduction: Int,
      $cashFlowProductionShift: Float,
      $sellerT12Revenue: Float,
      $sellerFiveYearGrowthRate: Float,
      $numberOfBuyers: Int,
      $buyer1T12Revenue: Float,
      $buyer1FiveYearGrowthRate: Float,
      $buyer1PercentagePurchasing: Float,
      $buyer2T12Revenue: Float,
      $buyer2FiveYearGrowthRate: Float,
      $buyer2PercentagePurchasing: Float,
      $buyerHasLumpSumPayment: Boolean,
      $buyerLumpSumAmount: Float,
      $buyerLumpSumYear: Int,
      $buyersFinancingAmount: Float,
      $buyerFinancingYears: Float,
      $buyerHasFixedPayments: Boolean,
      $buyerFixedPaymentYears: Int,
      $buyerFixedPaymentAmount: Float,
      $buyerFixedPaymentStartYear: Int,
      $buyerHasContinuingPayments: Boolean,
      $buyerContinuingPaymentYears: Int,
      $buyerContinuingPaymentStartYear: Int,
      $buyerContinuingPaymentAmount: Float,
      $buyerContinuingPayments: [Float!],
      $sellerManualTaxRate: Boolean,
      $sellerTaxRate: Float,
      $buyer1ManualTaxRate: Boolean,
      $buyer1TaxRate: Float,
      $buyer2ManualTaxRate: Boolean,
      $buyer2TaxRate: Float,
      $cashFlowFinancingInterestRate: Float,
      $hasCheckSwap: Boolean,
      $checkSwapYear: Int,
      $checkSwapAnnualBonus: Float,
      $checkSwapLoanEndYear: Int,
      $checkSwapLoanRepaymentAmount: Float,
      $buyer1AnnualExpenses: Float,
      $buyer1ExpenseGrowth: Float,
      $buyer2AnnualExpenses: Float,
      $buyer2ExpenseGrowth: Float,
      $cashflowSellerLifetimeCapitalGainsTax: Float,
      $consultingTotalCompensation: Float,
      $consultingLength: Int,
      $sellerNoteAmount: Float,
      $sellerNoteTerm: Int,
      $sellerNoteInterestRate: Float,
      $bdRetentionRate: Float,
    ) {
    updateCfa(
      id: $id,
      name: $name,
      cash_flow_length: $cashFlowLength,
      cash_flow_production: $cashFlowProduction,
      cash_flow_production_shift: $cashFlowProductionShift,
      seller_t12_revenue: $sellerT12Revenue,
      seller_five_year_growth_rate: $sellerFiveYearGrowthRate,
      number_of_buyers: $numberOfBuyers,
      buyer_1_t12_revenue: $buyer1T12Revenue,
      buyer_1_five_year_growth_rate: $buyer1FiveYearGrowthRate,
      buyer_1_percentage_purchasing: $buyer1PercentagePurchasing,
      buyer_2_t12_revenue: $buyer2T12Revenue,
      buyer_2_five_year_growth_rate: $buyer2FiveYearGrowthRate,
      buyer_2_percentage_purchasing: $buyer2PercentagePurchasing,
      buyer_has_lump_sum_payment: $buyerHasLumpSumPayment,
      buyer_lump_sum_amount: $buyerLumpSumAmount,
      buyer_lump_sum_year: $buyerLumpSumYear,
      buyers_financing_amount: $buyersFinancingAmount,
      buyer_financing_years: $buyerFinancingYears,
      buyer_has_fixed_payments: $buyerHasFixedPayments,
      buyer_fixed_payment_amount: $buyerFixedPaymentAmount,
      buyer_fixed_payment_years: $buyerFixedPaymentYears,
      buyer_fixed_payment_start_year: $buyerFixedPaymentStartYear,
      buyer_has_continuing_payments: $buyerHasContinuingPayments,
      buyer_continuing_payment_years: $buyerContinuingPaymentYears,
      buyer_continuing_payment_start_year: $buyerContinuingPaymentStartYear,
      buyer_continuing_payment_amount: $buyerContinuingPaymentAmount,
      buyer_continuing_payments: $buyerContinuingPayments,
      seller_manual_tax_rate: $sellerManualTaxRate,
      seller_tax_rate: $sellerTaxRate,
      buyer_1_manual_tax_rate: $buyer1ManualTaxRate,
      buyer_1_tax_rate: $buyer1TaxRate,
      buyer_2_manual_tax_rate: $buyer2ManualTaxRate,
      buyer_2_tax_rate: $buyer2TaxRate,
      cash_flow_financing_interest_rate: $cashFlowFinancingInterestRate,
      has_check_swap: $hasCheckSwap,
      check_swap_year: $checkSwapYear,
      check_swap_annual_bonus: $checkSwapAnnualBonus,
      check_swap_loan_end_year: $checkSwapLoanEndYear,
      check_swap_loan_repayment_amount: $checkSwapLoanRepaymentAmount,
      buyer_1_annual_expenses: $buyer1AnnualExpenses,
      buyer_1_expense_growth: $buyer1ExpenseGrowth,
      buyer_2_annual_expenses: $buyer2AnnualExpenses,
      buyer_2_expense_growth: $buyer2ExpenseGrowth,
      cashflow_seller_lifetime_capital_gains_tax: $cashflowSellerLifetimeCapitalGainsTax,
      consulting_total_compensation: $consultingTotalCompensation,
      consulting_length: $consultingLength,
      seller_note_amount: $sellerNoteAmount,
      seller_note_term: $sellerNoteTerm,
      seller_note_interest_rate: $sellerNoteInterestRate,
      bd_retention_rate: $bdRetentionRate,
    ) {
      id
      name
      friendlyName
      cashFlowLength
      cashFlowProduction
      cashFlowProductionShift
      sellerT12Revenue
      sellerFiveYearGrowthRate
      numberOfBuyers
      buyer1T12Revenue
      buyer1FiveYearGrowthRate
      buyer1PercentagePurchasing
      buyer2T12Revenue
      buyer2FiveYearGrowthRate
      buyer2PercentagePurchasing
      buyerHasLumpSumPayment
      buyerLumpSumAmount
      buyerLumpSumYear
      buyersFinancingAmount
      buyerFinancingYears
      buyerHasFixedPayments
      buyerFixedPaymentYears
      buyerFixedPaymentAmount
      buyerFixedPaymentStartYear
      buyerHasContinuingPayments
      buyerContinuingPaymentYears
      buyerContinuingPaymentStartYear
      buyerContinuingPaymentAmount
      buyerContinuingPayments
      sellerAverageCashFlow
      buyer1AverageCashFlow
      buyer2AverageCashFlow
      sellerManualTaxRate
      sellerTaxRate
      buyer1ManualTaxRate
      buyer1TaxRate
      buyer2ManualTaxRate
      buyer2TaxRate
      cashFlowFinancingInterestRate
      buyer1BreakEven
      buyer2BreakEven
      hasCheckSwap
      checkSwapYear
      checkSwapAnnualBonus
      checkSwapLoanEndYear
      checkSwapLoanRepaymentAmount
      buyer1AnnualExpenses
      buyer1ExpenseGrowth
      buyer2AnnualExpenses
      buyer2ExpenseGrowth
      purchasePrice
      lifetimeCapitalGainsException
      cashflowSellerLifetimeCapitalGainsTax
      consultingTotalCompensation
      consultingLength
      sellerNoteAmount
      sellerNoteTerm
      sellerNoteInterestRate
      sellerNotePaymentFrequency
      sellerNoteDefaultAmmortization
      bdRetentionRate
      illustration {
        sellerCashFlow {
          revenueGrowth
          personalProduction
          acquiredProduction
          cashFlowProductionShift
          payoutRate
          payout
          lumpSumPaymentReceived
          fixedPaymentReceived
          contingentPaymentReceived
          bookSalePaymentReceived
          pretaxNetIncome
          taxes
          totalTaxes
          downPayment
          afterTaxNetIncome
          freeCashFlow
          buyerHasFixedPayments
          taxRate
          transitionAssistanceLoanRepayment
          checkSwapPayment
          consultingAgreement
          consultingAgreementTaxes
          sellerNotePayment
          sellerCarriedNoteInterest
          sellerCarriedNotePrincipal
          earnOutTaxes
          downpaymentTaxes
          branchRetentionTaxes
          sellerNoteInterestTaxes
          sellerNotePrincipalTaxes
          transitionAssistanceLoanBonus
          cumulativeProceeds
        }
        buyer1CashFlow {
          revenueGrowth
          grossRevenue
          personalProduction
          aquiredProduction
          totalProduction
          payoutRate
          payout
          bookBuyPaymentTotal
          fixedPayment
          contingentPayment
          pretaxNetIncome
          taxes
          afterTaxNetIncome
          lumpSumPaymentOutOfPocket
          successionLoanRepayment
          debtServiceCoverageRatio
          freeCashFlow
          taxRate
          transitionAssistanceBonus
          branchExpenses
          consultingAgreement
          sellerNoteInterestPayment
          sellerNotePayment
          sellerNoteAmortization
          downpaymentLoanInterestTax
          downpaymentAmortizationTax
          taxableIncome
          downPaymentNote
          attritionRate
        }
        buyer2CashFlow {
          revenueGrowth
          grossRevenue
          personalProduction
          aquiredProduction
          totalProduction
          payoutRate
          payout
          bookBuyPaymentTotal
          fixedPayment
          contingentPayment
          pretaxNetIncome
          taxes
          afterTaxNetIncome
          lumpSumPaymentOutOfPocket
          successionLoanRepayment
          debtServiceCoverageRatio
          freeCashFlow
          taxRate
          transitionAssistanceBonus
          branchExpenses
          consultingAgreement
          sellerNoteInterestPayment
          sellerNotePayment
          sellerNoteAmortization
          downpaymentLoanInterestTax
          downpaymentAmortizationTax
          taxableIncome
          downPaymentNote
          attritionRate
        }
      }
    }
  }
`;

import { Edit, Printer, X, Save } from 'lucide-react';
import { useIntl } from 'react-intl';
import html2pdf from 'html2pdf.js';

// UI Canon components
import Card from '../../../../../../../ui/canon/Card';
import Button from '../../../../../../../ui/canon/Button';
import Tabs from '../../../../../../../ui/canon/Tabs';
import SuccessionSellerPerspective from './SuccessionSellerPerspective';
import SuccessionBuyerPerspective from './SuccessionBuyerPerspective';
import SuccessionDisclaimer from './SuccessionDisclaimer';
import CfaEditDrawer from './CfaEditDrawer';
import SingleColumnTemplate from '../../../../../../../ui/layouts/SingleColumnTemplate';

// TabPanel component for tab content
function TabPanel({ children, value, index, ...other }) {
  const isActive = value === index;
  return (
    <div
      role="tabpanel"
      hidden={!isActive}
      id={`cfa-tabpanel-${index}`}
      aria-labelledby={`cfa-tab-${index}`}
      {...other}
    >
      {isActive && children}
    </div>
  );
}

// Add purchase price calculation function (from original CFA)
export function calculatePurchasePrice(cf) {
  const sellerCfs = get(cf, 'illustration.sellerCashFlow', []);
  let purchasePrice = 0.0;

  purchasePrice += reduce(sellerCfs, (sum, cf) => sum + get(cf, 'lumpSumPaymentReceived', 0), 0);
  purchasePrice += reduce(sellerCfs, (sum, cf) => sum + get(cf, 'contingentPaymentReceived', 0), 0);
  purchasePrice += reduce(sellerCfs, (sum, cf) => sum + get(cf, 'sellerNotePayment', 0), 0);
  purchasePrice += reduce(sellerCfs, (sum, cf) => sum + get(cf, 'consultingAgreement', 0), 0);

  return purchasePrice;
}

export function calculatePurchasePriceOrdinaryTaxes(cf) {
  const sellerCfs = get(cf, 'illustration.sellerCashFlow', []);
  let purchasePrice = 0.0;

  purchasePrice += reduce(sellerCfs, (sum, cf) => sum + get(cf, 'bookSalePaymentReceived', 0), 0);

  return purchasePrice;
}

// Guided tour steps are built inside the component using intl

const CfaResults = ({ result, query, company, onEdit, onSave, updateCfaQuery, updateCfaResults }) => {
  const intl = useIntl();

  const CFA_TOUR_STEPS = [
    {
      id: 'summary-cards',
      title: intl.formatMessage({ id: 'succession.cash_flow_analysis.results.tour_key_metrics_title' }),
      description: intl.formatMessage({ id: 'succession.cash_flow_analysis.results.tour_key_metrics_description' }),
      targetSelector: '[data-tour="summary-cards"]',
      position: 'bottom'
    },
    {
      id: 'perspective-tabs',
      title: intl.formatMessage({ id: 'succession.cash_flow_analysis.results.tour_perspectives_title' }),
      description: intl.formatMessage({ id: 'succession.cash_flow_analysis.results.tour_perspectives_description' }),
      targetSelector: '[data-tour="perspective-tabs"]',
      position: 'right'
    },
    {
      id: 'edit-analysis',
      title: intl.formatMessage({ id: 'succession.cash_flow_analysis.results.tour_edit_inputs_title' }),
      description: intl.formatMessage({ id: 'succession.cash_flow_analysis.results.tour_edit_inputs_description' }),
      targetSelector: '[data-tour="edit-analysis"]',
      position: 'left'
    },
    {
      id: 'download-pdf',
      title: intl.formatMessage({ id: 'succession.cash_flow_analysis.results.tour_export_title' }),
      description: intl.formatMessage({ id: 'succession.cash_flow_analysis.results.tour_export_description' }),
      targetSelector: '[data-tour="download-pdf"]',
      position: 'left'
    }
  ];

  const [activeTab, setActiveTab] = useState('seller');
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [editData, setEditData] = useState(query || {});
  const [printing, setPrinting] = useState(false);
  const [printedOnLoad, setPrintedOnLoad] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [tourOpen, setTourOpen] = useState(false);
  const exportRef = useRef();

  // Check if user has seen the tour before
  useEffect(() => {
    const hasSeenTour = localStorage.getItem('cfa_results_tour_seen');
    if (!hasSeenTour) {
      // Delay tour start to let the page render
      const timer = setTimeout(() => setTourOpen(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleTourClose = () => {
    setTourOpen(false);
    localStorage.setItem('cfa_results_tour_seen', 'true');
  };

  const handleTourComplete = () => {
    localStorage.setItem('cfa_results_tour_seen', 'true');
  };

  // GraphQL mutation hook
  const [saveCfa] = useMutation(SAVE_CFA, {
    onCompleted: (data) => {
      console.log('CFA saved successfully:', data);
      
      // Update Redux state with new data (exactly like original SaveCfaButton)
      updateCfaQuery({ ...data.updateCfa, updating: true });
      updateCfaResults({ ...result, cashFlow: data.updateCfa });
      
      // Call the onSave callback if provided (like original SaveCfaButton's callOnSave)
      if (onSave) {
        onSave(data.updateCfa.friendlyName);
      }
      
      setSaving(false);
      setEditDrawerOpen(false);
      setSaveError(null);
      
      // Show success message (you could use a toast notification here)
      console.log('Save successful');
    },
    onError: (error) => {
      console.error('Error saving CFA:', error);
      setSaving(false);
      setSaveError(error.message);
    }
  });

  // Handle auto-print from URL parameter
  useEffect(() => {
    const params = qs.parse(window.location.search);
    if (!printedOnLoad && params.print) {
      setPrintedOnLoad(true);
      setPrinting(true);
      setTimeout(() => {
        handlePrint();
      }, 400);
    }
  }, [printedOnLoad]);

  // Get data from Redux state
  const cashFlow = get(result, 'cashFlow', {});
  const illustration = get(cashFlow, 'illustration', {});
  const sellerCashFlow = get(illustration, 'sellerCashFlow', []);
  const buyer1CashFlow = get(illustration, 'buyer1CashFlow', []);
  const buyer2CashFlow = get(illustration, 'buyer2CashFlow', []);
  const numberOfBuyers = parseInt(get(query, 'numberOfBuyers', 1), 10);
  
  // Calculate Total Proceeds (sum of all seller free cash flows)
  const totalProceeds = reduce(sellerCashFlow, (sum, cf) => sum + get(cf, 'freeCashFlow', 0), 0);
  
  // Company settings
  const cashFlowAnalysisWord = intl.locale === 'fr'
    ? (get(company, 'cash_flow_analysis_word_fr', '') || 'Analyse des flux de trésorerie')
    : (get(company, 'cash_flow_analysis_word', '') || 'Cash Flow Analysis');
  const features = get(company, 'company.data.features', []);
  const showOrdinaryTaxes = features.includes('cash_flow_ordinary_income_tax_rates');
  const showPurchasePrice = features.includes('cashflow_purchase_price');
  const showSellerNote = features.includes('cashflow_seller_note');
  const showConsultingAgreements = features.includes('cash_flow_consulting_agreements');
  const showCheckSwap = features.includes('check_swap');
  const showBranchExpenses = features.includes('cashflow_branch_expenses');
  const showBranchRetention = features.includes('cash_flow_branch_retention');
  const showLifeTimeCapitalGains = features.includes('cashflow_lifetime_capital_gains_exemption');
  const continuingPaymentsFINRAFootnote = features.includes('continuing_payments_FINRA_footnote');

  // Calculate purchase price using the same logic as original CFA
  let calculatedPurchasePrice = 0.0;
  if (showOrdinaryTaxes) {
    calculatedPurchasePrice = calculatePurchasePriceOrdinaryTaxes(cashFlow);
  } else {
    calculatedPurchasePrice = calculatePurchasePrice(cashFlow);
  }

  // Fallback to query.purchasePrice if calculation returns zero
  const displayPurchasePrice = calculatedPurchasePrice > 0 ? calculatedPurchasePrice : (query.purchasePrice || 0);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
  };

  const handleEditClick = () => {
    setEditData(query || {});
    setEditDrawerOpen(true);
  };

  const handleEditClose = () => {
    setEditDrawerOpen(false);
  };

  const handleEditSave = async () => {
    setSaving(true);
    setSaveError(null);
    
    try {
      // Merge editData with the original query, exactly like original SaveCfaButton
      const variables = {
        ...query,  // Start with original query
        ...editData,  // Override with edited values
        // Ensure id is included
        id: cashFlow.id || result.cashFlow?.id || query.id
      };
      
      console.log('Saving CFA with variables:', variables);
      
      // Call the GraphQL mutation (exactly like original SaveCfaButton)
      await saveCfa({ variables });
      
    } catch (error) {
      console.error('Error in handleEditSave:', error);
      setSaving(false);
      setSaveError(error.message);
    }
  };

  const handlePrint = () => {
    setPrinting(true);

    // Wait for the print layout to render before generating PDF
    setTimeout(() => {
      generatePDF();
    }, 100);
  };

  const generatePDF = () => {
    // Check if exportRef is available
    if (!exportRef.current) {
      console.error('Export ref not available');
      setPrinting(false);
      return;
    }

    // Hide charts and other non-printable elements
    const chartElements = document.querySelectorAll('.recharts-wrapper, .recharts-responsive-container, .chart-section');
    const heroCard = document.querySelector('.MuiPaper-root[class*="heroCard"]');
    const tabsContainer = document.querySelector('.MuiTabs-root');
    const summaryCards = document.querySelectorAll('.MuiCard-root');
    const chartSections = document.querySelectorAll('.chart-section, .mb-8');
    
    const elementsToHide = [heroCard, tabsContainer, ...summaryCards, ...chartElements, ...chartSections].filter(Boolean);
    const originalStyles = elementsToHide.map(element => element.style.cssText);

    elementsToHide.forEach(element => {
      if (element) {
        element.style.display = 'none';
        element.style.height = '0';
        element.style.margin = '0';
        element.style.padding = '0';
      }
    });

    // DIRECTLY MODIFY ALL ELEMENTS FOR PERFECT STYLING
    const allTables = document.querySelectorAll('table');
    const allThs = document.querySelectorAll('th');
    const allTds = document.querySelectorAll('td');
    const allH3s = document.querySelectorAll('h3');
    const printHeader = document.querySelector('.print-header');
    const printSummary = document.querySelector('.print-summary');
    const printSummaryItems = document.querySelectorAll('.print-summary-item');
    const printSummaryLabels = document.querySelectorAll('.print-summary-label');
    const printSummaryValues = document.querySelectorAll('.print-summary-value');
    const printableContainers = document.querySelectorAll('.printable');
    
    // Store original styles to restore later
    const originalTableStyles = Array.from(allTables).map(el => el.style.cssText);
    const originalThStyles = Array.from(allThs).map(el => el.style.cssText);
    const originalTdStyles = Array.from(allTds).map(el => el.style.cssText);
    const originalH3Styles = Array.from(allH3s).map(el => el.style.cssText);
    const originalPrintHeaderStyle = printHeader?.style.cssText || '';
    const originalPrintSummaryStyle = printSummary?.style.cssText || '';
    const originalPrintableStyles = Array.from(printableContainers).map(el => el.style.cssText);
    
    // Style the print header
    if (printHeader) {
      printHeader.style.textAlign = 'center';
      printHeader.style.marginBottom = '20px';
      printHeader.style.padding = '20px 0';
      printHeader.style.borderBottom = '2px solid var(--succession-border)';
      
      const h1 = printHeader.querySelector('h1');
      const h2 = printHeader.querySelector('h2');
      const p = printHeader.querySelector('p');
      
      if (h1) {
        h1.style.fontSize = '28px';
        h1.style.fontWeight = '600';
        h1.style.margin = '0 0 8px 0';
        h1.style.color = 'var(--succession-text-primary)';
      }
      if (h2) {
        h2.style.fontSize = '16px';
        h2.style.fontWeight = 'normal';
        h2.style.margin = '0 0 8px 0';
        h2.style.color = 'var(--succession-text-secondary)';
      }
      if (p) {
        p.style.fontSize = '12px';
        p.style.margin = '0';
        p.style.color = 'var(--succession-text-muted)';
      }
    }
    
    // Style the print summary
    if (printSummary) {
      printSummary.style.display = 'flex';
      printSummary.style.justifyContent = 'space-around';
      printSummary.style.margin = '8px 0 12px 0';
      printSummary.style.padding = '15px';
      printSummary.style.background = 'var(--succession-bg-secondary)';
      printSummary.style.borderRadius = '6px';
      printSummary.style.border = '1px solid var(--succession-border)';
    }
    
    printSummaryLabels.forEach(label => {
      label.style.fontSize = '12px';
      label.style.color = 'var(--succession-text-secondary)';
      label.style.marginBottom = '4px';
      label.style.fontWeight = '500';
    });
    
    printSummaryValues.forEach(value => {
      value.style.fontSize = '16px';
      value.style.fontWeight = 'bold';
      value.style.color = 'var(--succession-text-primary)';
      value.style.margin = '0';
    });
    
    // ELIMINATE ALL GAPS - set printable containers with zero margin and compact everything
    printableContainers.forEach(container => {
      container.style.margin = '0';
      container.style.padding = '0';
      
      // Find any divs with spacing classes and eliminate them
      const spacingDivs = container.querySelectorAll('.mb-8, .mt-4, .mb-4, .mt-8');
      spacingDivs.forEach(div => {
        div.style.display = 'none';
      });
    });
    
    // Apply large fonts directly to DOM elements
    allTables.forEach(table => {
      table.style.fontSize = '16px';
      table.style.fontFamily = 'Arial, sans-serif';
      table.style.width = '100%';
      table.style.borderCollapse = 'collapse';
      table.style.margin = '0 0 15px 0'; // Only bottom margin
      table.style.display = 'table'; // Force table display
    });
    
    allThs.forEach(th => {
      th.style.fontSize = '16px';
      th.style.padding = '12px 8px';
      th.style.fontWeight = 'bold';
      th.style.background = 'var(--succession-primary)';
      th.style.color = 'white';
      th.style.border = '1px solid var(--succession-border)';
    });
    
    allTds.forEach(td => {
      td.style.fontSize = '14px';
      td.style.padding = '10px 8px';
      td.style.border = '1px solid var(--succession-border)';
      td.style.lineHeight = '1.3';
    });
    
    // SECTION HEADERS - zero margin, stick to tables
    allH3s.forEach(h3 => {
      h3.style.fontSize = '20px';
      h3.style.fontWeight = 'bold';
      h3.style.margin = '15px 0 0 0'; // Only top margin, NO bottom margin
      h3.style.padding = '8px 0 4px 0';
      h3.style.borderBottom = '3px solid var(--succession-primary)';
      h3.style.color = 'var(--succession-text-primary)';
    });

    // Remove any existing print styles that make things tiny
    const existingPrintStyles = document.querySelectorAll('style[data-styled], style');
    const disabledStyles = [];
    existingPrintStyles.forEach(style => {
      if (style.textContent.includes('@media print') || style.textContent.includes('font-size: 8px')) {
        style.disabled = true;
        disabledStyles.push(style);
      }
    });

    const opt = {
      pagebreak: { 
        mode: ['css', 'legacy'],
        avoid: ['.print-summary', '.print-header']
      },
      jsPDF: { 
        unit: 'in', 
        format: 'letter', 
        orientation: 'portrait', 
        quality: 2.0
      },
      margin: [0.3, 0.3, 0.3, 0.3],
      html2canvas: { 
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        scrollX: 0,
        scrollY: 0,
        windowWidth: 1200,
        windowHeight: 6000,
        width: 1200,
        height: null, // Let it auto-calculate height
        onrendered: function(canvas) {
          console.log('Canvas height:', canvas.height);
        }
      }
    };

    const worker = html2pdf().set(opt).from(exportRef.current).save(`${cashFlow.name || 'Cash_Flow_Analysis'}.pdf`);

    worker.then(() => {
      // Restore all original styles
      elementsToHide.forEach((element, index) => {
        if (element) {
          element.style.cssText = originalStyles[index];
        }
      });
      
      // Restore all modified styles
      allTables.forEach((table, index) => {
        table.style.cssText = originalTableStyles[index];
      });
      allThs.forEach((th, index) => {
        th.style.cssText = originalThStyles[index];
      });
      allTds.forEach((td, index) => {
        td.style.cssText = originalTdStyles[index];
      });
      allH3s.forEach((h3, index) => {
        h3.style.cssText = originalH3Styles[index];
      });
      if (printHeader) {
        printHeader.style.cssText = originalPrintHeaderStyle;
      }
      if (printSummary) {
        printSummary.style.cssText = originalPrintSummaryStyle;
      }
      printableContainers.forEach((container, index) => {
        container.style.cssText = originalPrintableStyles[index];
      });
      
      // Re-enable disabled styles
      disabledStyles.forEach(style => {
        style.disabled = false;
      });
      
      setPrinting(false);
    }).catch((error) => {
      console.error('Error generating PDF:', error);
      // Still restore everything on error
      elementsToHide.forEach((element, index) => {
        if (element) {
          element.style.cssText = originalStyles[index];
        }
      });
      allTables.forEach((table, index) => {
        table.style.cssText = originalTableStyles[index];
      });
      allThs.forEach((th, index) => {
        th.style.cssText = originalThStyles[index];
      });
      allTds.forEach((td, index) => {
        td.style.cssText = originalTdStyles[index];
      });
      allH3s.forEach((h3, index) => {
        h3.style.cssText = originalH3Styles[index];
      });
      if (printHeader) {
        printHeader.style.cssText = originalPrintHeaderStyle;
      }
      if (printSummary) {
        printSummary.style.cssText = originalPrintSummaryStyle;
      }
      printableContainers.forEach((container, index) => {
        container.style.cssText = originalPrintableStyles[index];
      });
      disabledStyles.forEach(style => {
        style.disabled = false;
      });
      setPrinting(false);
    });
  };

  const handleInputChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Format currency for display
  const formatCurrency = (value) => {
    const sym = currencySymbol();
    if (!value || isNaN(value)) return `${sym}0`;
    const cur = (typeof window !== 'undefined' && window.currency) || 'USD';
    const locale = cur === 'GBP' ? 'en-GB' : 'en-US';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: cur,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Calculate multiple for total proceeds (revenue multiple)
  const calculateMultiple = () => {
    const sellerT12 = get(query, 'sellerT12Revenue', 0);
    if (sellerT12 > 0 && totalProceeds > 0) {
      const multiple = totalProceeds / sellerT12;
      return `(${multiple.toFixed(2)}x multiple)`;
    }
    return '';
  };

  // Format break even year
  const formatBreakEvenYear = (breakEven) => {
    if (!breakEven || breakEven <= 0) return 'NA';
    return `Year ${breakEven}`;
  };



  const pageActions = (
    <div className="flex gap-3">
      <Button
        as="a"
        href="/succession/calculators/cfa"
        variant="outline"
        className="no-underline"
      >
        <i className="fas fa-arrow-left mr-2"></i>
        {intl.formatMessage({ id: 'succession.cash_flow_analysis.results.back_to_list' })}
      </Button>
      <Button
        data-tour="edit-analysis"
        variant="outline"
        onClick={handleEditClick}
      >
        <Edit className="w-4 h-4 mr-2" />
        {intl.formatMessage({ id: 'succession.cash_flow_analysis.results.edit_analysis' })}
      </Button>
      <Button
        data-tour="download-pdf"
        variant="outline"
        onClick={handlePrint}
        disabled={printing}
      >
        <Printer className="w-4 h-4 mr-2" />
        {printing ? intl.formatMessage({ id: 'succession.cash_flow_analysis.results.generating_pdf' }) : intl.formatMessage({ id: 'succession.cash_flow_analysis.results.download_pdf' })}
      </Button>
    </div>
  );

  return (
    <>
      {!printing ? (
        <SingleColumnTemplate 
          variant="wide"
          title={cashFlow.name || cashFlowAnalysisWord}
          subtitle={intl.formatMessage({ id: 'succession.cash_flow_analysis.results.buyers_subtitle' }, { count: numberOfBuyers })}
          actions={pageActions}
        >
          {/* Summary Metrics */}
          <div data-tour="summary-cards" className={`grid gap-4 mb-6 ${numberOfBuyers > 1 ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-5' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'}`}>
            <Card size="sm" className="h-full flex flex-col min-h-[100px]">
              <p className="text-sm text-succession-text-secondary mb-2">
                {intl.formatMessage({ id: 'succession.cash_flow_analysis.results.total_proceeds' })}
              </p>
              <h2 className="text-xl font-semibold text-succession-text-primary">
                {formatCurrency(totalProceeds)}
              </h2>
              <span className="text-xs text-succession-text-muted min-h-[18px]">
                {calculateMultiple()}
              </span>
            </Card>
            <Card size="sm" className="h-full flex flex-col min-h-[100px]">
              <p className="text-sm text-succession-text-secondary mb-2">
                {intl.formatMessage({ id: 'succession.cash_flow_analysis.results.seller_avg_cash_flow' })}
              </p>
              <h2 className="text-xl font-semibold text-succession-text-primary">
                {formatCurrency(get(result, 'cashFlow.sellerAverageCashFlow', 0))}
              </h2>
              <div className="min-h-[18px]" />
            </Card>
            <Card size="sm" className="h-full flex flex-col min-h-[100px]">
              <p className="text-sm text-succession-text-secondary mb-2">
                {intl.formatMessage({ id: 'succession.cash_flow_analysis.results.buyer1_avg_cash_flow' })}
              </p>
              <h2 className="text-xl font-semibold text-succession-text-primary">
                {formatCurrency(get(result, 'cashFlow.buyer1AverageCashFlow', 0))}
              </h2>
              <div className="min-h-[18px]" />
            </Card>
            {numberOfBuyers > 1 && (
              <Card size="sm" className="h-full flex flex-col min-h-[100px]">
                <p className="text-sm text-succession-text-secondary mb-2">
                  {intl.formatMessage({ id: 'succession.cash_flow_analysis.results.buyer2_avg_cash_flow' })}
                </p>
                <h2 className="text-xl font-semibold text-succession-text-primary">
                  {formatCurrency(get(result, 'cashFlow.buyer2AverageCashFlow', 0))}
                </h2>
                <div className="min-h-[18px]" />
              </Card>
            )}
            <Card size="sm" className="h-full flex flex-col min-h-[100px]">
              <p className="text-sm text-succession-text-secondary mb-2">
                {numberOfBuyers > 1 ? intl.formatMessage({ id: 'succession.cash_flow_analysis.results.buyer_break_even' }) : intl.formatMessage({ id: 'succession.cash_flow_analysis.results.buyer1_break_even' })}
              </p>
              <h2 className="text-xl font-semibold text-succession-text-primary">
                {numberOfBuyers > 1 
                  ? `${formatBreakEvenYear(get(result, 'cashFlow.buyer1BreakEven', 0))} / ${formatBreakEvenYear(get(result, 'cashFlow.buyer2BreakEven', 0))}`
                  : formatBreakEvenYear(get(result, 'cashFlow.buyer1BreakEven', 0))
                }
              </h2>
              <div className="min-h-[18px]" />
            </Card>
          </div>

          {/* Tabs Navigation */}
          <Card variant="outlined" className="mb-6">
            <Tabs
              data-tour="perspective-tabs"
              tabs={[
                { id: 'seller', label: intl.formatMessage({ id: 'succession.cash_flow_analysis.results.tab_seller' }) },
                { id: 'buyer1', label: intl.formatMessage({ id: 'succession.cash_flow_analysis.results.tab_buyer1' }) },
                ...(numberOfBuyers > 1 ? [{ id: 'buyer2', label: intl.formatMessage({ id: 'succession.cash_flow_analysis.results.tab_buyer2' }) }] : []),
                { id: 'disclaimer', label: intl.formatMessage({ id: 'succession.cash_flow_analysis.results.tab_disclaimer' }) },
              ]}
              value={activeTab}
              onChange={handleTabChange}
              showDescription={false}
            />

            <div className="p-6">
              <TabPanel value={activeTab} index="seller">
                <SuccessionSellerPerspective />
              </TabPanel>
              <TabPanel value={activeTab} index="buyer1">
                <SuccessionBuyerPerspective index={1} />
              </TabPanel>
              {numberOfBuyers > 1 && (
                <TabPanel value={activeTab} index="buyer2">
                  <SuccessionBuyerPerspective index={2} />
                </TabPanel>
              )}
              <TabPanel value={activeTab} index="disclaimer">
                <SuccessionDisclaimer />
              </TabPanel>
            </div>
          </Card>
        </SingleColumnTemplate>
      ) : (
        // Print Layout
        <div ref={exportRef} className="text-[10px] mx-[0.2in] p-0">
          {/* Clean Print Header */}
          <div className="print-header">
            <h1>{cashFlowAnalysisWord}</h1>
            <h2>{cashFlow.name || intl.formatMessage({ id: 'succession.cash_flow_analysis.results.print_professional_assessment' })}</h2>
            <p>{intl.formatMessage({ id: 'succession.cash_flow_analysis.results.print_generated_on' })} {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>

          {/* Executive Summary */}
          <div className="print-summary">
            <div className="print-summary-item">
              <div className="print-summary-label">{intl.formatMessage({ id: 'succession.cash_flow_analysis.results.total_proceeds' })}</div>
              <div className="print-summary-value currency">
                {formatCurrency(totalProceeds)}
                {calculateMultiple() && <div className="text-[10px] text-[var(--succession-text-secondary)]">{calculateMultiple()}</div>}
              </div>
            </div>
            <div className="print-summary-item">
              <div className="print-summary-label">{intl.formatMessage({ id: 'succession.cash_flow_analysis.results.print_seller_avg' })}</div>
              <div className="print-summary-value currency">{formatCurrency(get(result, 'cashFlow.sellerAverageCashFlow', 0))}</div>
            </div>
            <div className="print-summary-item">
              <div className="print-summary-label">{intl.formatMessage({ id: 'succession.cash_flow_analysis.results.print_buyer1_avg' })}</div>
              <div className="print-summary-value currency">{formatCurrency(get(result, 'cashFlow.buyer1AverageCashFlow', 0))}</div>
            </div>
            {numberOfBuyers > 1 && (
              <div className="print-summary-item">
                <div className="print-summary-label">{intl.formatMessage({ id: 'succession.cash_flow_analysis.results.print_buyer2_avg' })}</div>
                <div className="print-summary-value currency">{formatCurrency(get(result, 'cashFlow.buyer2AverageCashFlow', 0))}</div>
              </div>
            )}
            <div className="print-summary-item">
              <div className="print-summary-label">{intl.formatMessage({ id: 'succession.cash_flow_analysis.results.print_break_even' })}</div>
              <div className="print-summary-value">
                {numberOfBuyers > 1 
                  ? `${formatBreakEvenYear(get(result, 'cashFlow.buyer1BreakEven', 0))} / ${formatBreakEvenYear(get(result, 'cashFlow.buyer2BreakEven', 0))}`
                  : formatBreakEvenYear(get(result, 'cashFlow.buyer1BreakEven', 0))
                }
              </div>
            </div>
            <div className="print-summary-item">
              <div className="print-summary-label">{intl.formatMessage({ id: 'succession.cash_flow_analysis.results.print_analysis_period' })}</div>
              <div className="print-summary-value">{get(result, 'cashFlow.cashFlowLength', 0)} {intl.formatMessage({ id: 'succession.cash_flow_analysis.results.print_years' })}</div>
            </div>
          </div>

          {/* Print Content - All perspectives in sequence */}
          <div className="printable">
            <h3>{intl.formatMessage({ id: 'succession.cash_flow_analysis.results.print_seller_perspective' })}</h3>
            <SuccessionSellerPerspective />
          </div>

          <div className="printable">
            <h3>{intl.formatMessage({ id: 'succession.cash_flow_analysis.results.print_buyer1_perspective' })}</h3>
            <SuccessionBuyerPerspective index={1} />
          </div>

          {numberOfBuyers > 1 && (
            <div className="printable">
              <h3>{intl.formatMessage({ id: 'succession.cash_flow_analysis.results.print_buyer2_perspective' })}</h3>
              <SuccessionBuyerPerspective index={2} />
            </div>
          )}
          
          <div className="printable disclaimer">
            <SuccessionDisclaimer />
          </div>
        </div>
      )}

            {/* Edit Drawer */}
      <CfaEditDrawer
        open={editDrawerOpen}
        onClose={handleEditClose}
        editData={editData}
        onInputChange={handleInputChange}
        onSave={handleEditSave}
        saving={saving}
        saveError={saveError}
        // Feature flags
        showOrdinaryTaxes={showOrdinaryTaxes}
        showPurchasePrice={showPurchasePrice}
        showSellerNote={showSellerNote}
        showConsultingAgreements={showConsultingAgreements}
        showCheckSwap={showCheckSwap}
        showBranchExpenses={showBranchExpenses}
        showBranchRetention={showBranchRetention}
        showLifeTimeCapitalGains={showLifeTimeCapitalGains}
        continuingPaymentsFINRAFootnote={continuingPaymentsFINRAFootnote}
        // Calculated values
        displayPurchasePrice={displayPurchasePrice}
        calculatedPurchasePrice={calculatedPurchasePrice}
        numberOfBuyers={numberOfBuyers}
        formatCurrency={formatCurrency}
      />

      {/* Guided Tour */}
      <GuidedTour
        isOpen={tourOpen}
        steps={CFA_TOUR_STEPS}
        onClose={handleTourClose}
        onComplete={handleTourComplete}
        onReplayRequest={() => setTourOpen(true)}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  result: state.cfa?.result || {},
  query: state.cfa?.query || {},
  company: state.company || {},
});

export default connect(mapStateToProps, cfaActionCreators)(CfaResults); 